import { useLocation, useHistory } from 'react-router-dom';
import { BarChart2, List, Settings, Plus, ShieldCheck, LogOut, HelpCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@utils/Cn.js';
import { useAuthStore } from '@features/auth/store/AuthStore.js';
import { useLogoutMutation } from '@features/auth/hooks/useAuth.js';

interface BottomBarProps {
  className?: string;
}

export default function BottomBar({ className = '' }: BottomBarProps) {
  const location = useLocation();
  const history = useHistory();
  const user = useAuthStore((s) => s.user);
  const currentPath = location.pathname;
  const logoutMutation = useLogoutMutation();

  function handleLogout() {
    void logoutMutation.mutateAsync().then(() => {
      history.push('/login');
    });
  }

  const menuItems =
    user?.role === 'ADMIN'
      ? [
          {
            label: 'Users',
            path: '/users',
            icon: ShieldCheck,
            isActive: () => currentPath === '/users',
          },
          {
            label: logoutMutation.isPending ? 'Logging out...' : 'Logout',
            path: '#logout',
            icon: LogOut,
            isActive: () => false,
            onClick: handleLogout,
            disabled: logoutMutation.isPending,
          },
        ]
      : [
          {
            label: 'Renewals',
            path: '/policies',
            icon: List,
            isActive: () =>
              currentPath.startsWith('/policies') &&
              currentPath !== '/policies/new' &&
              !currentPath.includes('/edit'),
          },
          {
            label: 'Enquiries',
            path: '/enquiries',
            icon: HelpCircle,
            isActive: () =>
              currentPath.startsWith('/enquiries') &&
              currentPath !== '/enquiries/new' &&
              !currentPath.includes('/edit'),
          },
          {
            label: 'Dashboard',
            path: '/dashboard',
            icon: BarChart2,
            isActive: () => currentPath === '/dashboard',
          },
          {
            label: 'Settings',
            path: '/settings',
            icon: Settings,
            isActive: () => currentPath === '/settings',
          },
        ];

  const isFormPage =
    currentPath === '/policies/new' ||
    currentPath.includes('/edit') ||
    currentPath === '/enquiries/new';
  const isDashboard = currentPath === '/dashboard';
  const showFloatingAdd = !isFormPage && !isDashboard && user?.role !== 'ADMIN';

  return (
    <>
      {/* Floating Add button positioned above bottom bar on the right */}
      <AnimatePresence>
        {showFloatingAdd && (
          <motion.button
            key="floating-add-btn"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.92 }}
            onClick={() => {
              const target =
                currentPath.startsWith('/enquiries') && currentPath !== '/enquiries/new'
                  ? '/enquiries/new'
                  : '/policies/new';
              history.push(target);
            }}
            className={cn(
              'fixed bottom-24 right-5 z-40 flex h-13 w-13 items-center justify-center rounded-2xl bg-slate text-white shadow-[0_8px_20px_rgba(15,118,110,0.35)] dark:shadow-[0_8px_24px_rgba(45,212,191,0.45)] border border-white/10 cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate/40 md:hidden',
            )}
            aria-label="Add new policy renewal"
          >
            <Plus size={22} strokeWidth={2.5} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Floating Bottom Navigation Bar (balanced with 3 tabs) */}
      {!isFormPage && (
        <div
          className={cn(
            'fixed bottom-5 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-md h-16 bg-surface/85 border border-line/40 rounded-2xl shadow-[0_12px_40px_rgba(13,23,44,0.16)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.55)] backdrop-blur-xl flex items-center justify-between px-3 z-40 select-none',
            className,
          )}
        >
          {menuItems.map((item) => {
            const active = item.isActive();
            const Icon = item.icon;

            return (
              <motion.button
                key={item.path}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  if ('onClick' in item) {
                    item.onClick();
                  } else {
                    history.push(item.path);
                  }
                }}
                disabled={'disabled' in item ? item.disabled : false}
                className={cn(
                  'relative flex flex-col items-center justify-center flex-1 h-full py-1 text-[10px] font-bold transition-colors cursor-pointer focus:outline-none',
                  active ? 'text-slate font-extrabold' : 'text-ink-soft hover:text-ink',
                )}
              >
                {/* Animated pill background behind active item */}
                <AnimatePresence>
                  {active && (
                    <motion.span
                      layoutId="bottom-active-pill"
                      className="absolute inset-x-4 top-2.5 bottom-2.5 rounded-xl bg-slate/8 dark:bg-slate/15 -z-10"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    />
                  )}
                </AnimatePresence>

                <Icon
                  size={18}
                  strokeWidth={active ? 2.25 : 1.75}
                  className={cn(
                    'transition-transform mb-0.5',
                    active ? 'text-slate scale-105' : 'text-ink-faint group-hover:text-ink',
                  )}
                />
                <span className="font-sans leading-none tracking-tight">{item.label}</span>
              </motion.button>
            );
          })}
        </div>
      )}
    </>
  );
}
